import type { ProjectWhereInput } from '@gql/graphql';
import type { StringOption } from '@models/Option';

export interface ProjectFiltersState {
    search: string;
    statusIds: string[];
    typeIds: string[];
}

export const DEFAULT_PROJECT_FILTERS: ProjectFiltersState = {
    search: '',
    statusIds: [],
    typeIds: [],
};

function createRelationIdFilter<T extends keyof ProjectFiltersState>(
    key: T,
    ids: ProjectFiltersState[T],
): ProjectWhereInput | undefined {
    if (!Array.isArray(ids) || ids.length === 0) {
        return undefined;
    }

    switch (key) {
        case 'statusIds':
            return {
                hasStatusWith: [{ idIn: ids }],
            };
        case 'typeIds':
            return {
                hasTypeWith: [{ idIn: ids }],
            };
        default:
            return undefined;
    }
}

function buildFreeTextFilter(search: string): ProjectWhereInput | undefined {
    const value = search.trim();

    if (!value) {
        return undefined;
    }

    return {
        or: [
            { nameContainsFold: value },
            { descriptionContainsFold: value },
            { customerNameContainsFold: value },
            { locationCityContainsFold: value },
            { locationCountryContainsFold: value },
            { plantNameContainsFold: value },
            {
                hasOrganizationWith: [{ nameContainsFold: value }],
            },
            {
                hasMilestoneWith: [{ nameContainsFold: value }],
            },
            {
                hasStatusWith: [{ nameContainsFold: value }],
            },
            {
                hasTypeWith: [{ nameContainsFold: value }],
            },
            {
                hasCreatorWith: [
                    {
                        or: [
                            { firstNameContainsFold: value },
                            { lastNameContainsFold: value },
                            { emailContainsFold: value },
                            { gidContainsFold: value },
                        ],
                    },
                ],
            },
        ],
    };
}

export function buildProjectsWhere(
    filters: ProjectFiltersState,
): ProjectWhereInput | undefined {
    const clauses = [
        buildFreeTextFilter(filters.search),
        createRelationIdFilter('statusIds', filters.statusIds),
        createRelationIdFilter('typeIds', filters.typeIds),
    ].filter(Boolean) as ProjectWhereInput[];

    if (clauses.length === 0) {
        return undefined;
    }

    return clauses.length === 1 ? clauses[0] : { and: clauses };
}

export function hasActiveProjectFilters(filters: ProjectFiltersState): boolean {
    return (
        filters.search.trim().length > 0 ||
        filters.statusIds.length > 0 ||
        filters.typeIds.length > 0
    );
}

export function sortStringOptions(options: StringOption[]): StringOption[] {
    return [...options].sort((left, right) =>
        left.label.localeCompare(right.label),
    );
}
